import { Component, computed, inject } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { catchError, distinctUntilChanged, of, startWith, switchMap } from 'rxjs';

import { SessionStore } from '../../core/auth/session.store';
import { TranslationService } from '../../core/i18n/translation.service';
import { UserAdminApi } from '../../pages/users/services/user-admin.api';

@Component({
  selector: 'app-account-profile',
  host: { class: 'app-account-profile' },
  templateUrl: './account-profile.component.html',
})
export class AccountProfileComponent {
  private readonly sessionStore = inject(SessionStore);
  private readonly usersApi = inject(UserAdminApi);
  private readonly i18n = inject(TranslationService);
  private readonly userCode = computed(() => this.sessionStore.session()?.userCode);
  private readonly loadedProfile = toSignal(
    toObservable(this.userCode).pipe(
      distinctUntilChanged(),
      switchMap((userCode) =>
        userCode
          ? this.usersApi.getUser(userCode).pipe(startWith(null), catchError(() => of(null)))
          : of(null),
      ),
    ),
    { initialValue: null },
  );
  private readonly profile = computed(() => {
    const profile = this.loadedProfile();
    return profile?.user_code === this.userCode() ? profile : null;
  });
  private readonly nameParts = computed(() =>
    [this.profile()?.first_name?.trim(), this.profile()?.last_name?.trim()].filter(
      (part): part is string => !!part,
    ),
  );

  protected readonly displayName = computed(
    () => this.nameParts().join(' ') || this.sessionStore.session()?.login || this.i18n.t('auth.account.user'),
  );
  protected readonly initials = computed(() =>
    this.nameParts().map((part) => Array.from(part)[0]).join('').toLocaleUpperCase(),
  );
  protected readonly roleLabel = computed(() => {
    const role = this.profile()?.role ?? this.sessionStore.session()?.role;
    return this.i18n.t(role ? `auth.account.roles.${role}` : 'auth.account.user');
  });
}
